import { useEffect, useState, useRef } from 'react'
import { Shield } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'
import PreviewAuthModal from './PreviewAuthModal'

interface ProtectedTenantRouteProps {
  children: React.ReactNode
}

/**
 * Protects preview and last (rollback) sites with authentication
 * Live sites remain public
 */
const ProtectedTenantRoute: React.FC<ProtectedTenantRouteProps> = ({ children }) => {
  const { currentTenant } = useTenant()
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null)
  const [version, setVersion] = useState<string>('live')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const isCheckingRef = useRef(false)
  const hasCheckedRef = useRef(false)

  // Helper to get cookie value
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null
    }
    return null
  }

  useEffect(() => {
    // Prevent multiple simultaneous checks
    if (isCheckingRef.current) return

    // DEBUG: Log what we're seeing
    // @ts-ignore
    console.log('🔒 ProtectedTenantRoute checking...', {
      configExists: typeof __TENANT_CONFIG__ !== 'undefined',
      // @ts-ignore
      config: typeof __TENANT_CONFIG__ !== 'undefined' ? __TENANT_CONFIG__ : 'undefined',
      currentTenant: currentTenant?.id
    })

    // Get version from tenant config
    // @ts-ignore - __TENANT_CONFIG__ is injected at build time
    if (typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__.version) {
      const siteVersion = __TENANT_CONFIG__.version
      console.log('🔒 ProtectedTenantRoute: version =', siteVersion)
      setVersion(siteVersion)

      // Live sites are always public - no auth required
      if (siteVersion === 'live') {
        console.log('✅ Live site - allowing access without auth check')
        setIsAuthorized(true)
        hasCheckedRef.current = true
        return
      }

      // Preview and last sites require authentication
      // Only check if we have tenant data or if we haven't checked yet
      if ((siteVersion === 'preview' || siteVersion === 'last') && !hasCheckedRef.current) {
        // If we don't have tenant data yet, wait for it
        if (!currentTenant) {
          return
        }

        isCheckingRef.current = true
        console.log(`🔒 ${siteVersion} site - checking authorization...`)

        const checkAuth = async () => {
          try {
            // Check if user is authenticated (check both cookie and localStorage)
            const authToken = getCookie('auth-token') || localStorage.getItem('auth-token')
            if (!authToken) {
              console.log('❌ No auth token - showing auth modal')
              setIsAuthorized(false)
              setShowAuthModal(true)
              hasCheckedRef.current = true
              isCheckingRef.current = false
              return
            }

            // Validate user's domain matches tenant domain
            const response = await fetch('/api/auth/validate', {
              method: 'GET',
              credentials: 'include'
            })

            if (response.ok) {
              const data = await response.json()
              const userEmail = data.email || ''
              const parts = userEmail.split('@')
              const userDomain = parts.length > 1 ? parts[1] : ''

              // Check if user's domain matches tenant's domain
              if (currentTenant && userDomain === currentTenant.domain) {
                console.log(`✅ Authorized: ${userEmail} can access ${currentTenant.domain} ${siteVersion} site`)
                setIsAuthorized(true)
                setShowAuthModal(false)
              } else {
                console.log(`❌ Unauthorized: ${userEmail} cannot access ${currentTenant?.domain} ${siteVersion} site`)
                setIsAuthorized(false)
                setShowAuthModal(true)
              }
            } else {
              console.log('❌ Token validation failed - showing auth modal')
              setIsAuthorized(false)
              setShowAuthModal(true)
            }
          } catch (error) {
            console.error('Authorization check failed:', error)
            setIsAuthorized(false)
            setShowAuthModal(true)
          } finally {
            hasCheckedRef.current = true
            isCheckingRef.current = false
          }
        }

        checkAuth()
      }
    } else {
      // No tenant config (platform site) - allow access
      setIsAuthorized(true)
      hasCheckedRef.current = true
    }
  }, [currentTenant])

  // Loading state while checking authorization
  if (isAuthorized === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    )
  }

  // Show auth modal when needed
  const handleAuthSuccess = () => {
    setShowAuthModal(false)
    setIsAuthorized(true)
    hasCheckedRef.current = true
  }

  // Render children with modal overlay if needed
  return (
    <>
      {children}
      {showAuthModal && currentTenant && (version === 'preview' || version === 'last') && (
        <PreviewAuthModal
          tenantDomain={currentTenant.domain}
          version={version as 'preview' | 'last'}
          onSuccess={handleAuthSuccess}
        />
      )}
    </>
  )
}

export default ProtectedTenantRoute
